import { useCallback, useEffect, useState, useMemo } from 'react'
import { ArrowLeft, LayoutGrid, BarChart3, DollarSign, MoreHorizontal, Plane } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { MapboxScene, type MapboxSceneRefs } from '@/components/3d'
import type { MapboxCustomLayerService } from '@/services/3d'
import { useAppContext, useMapBuildings, useInitialViewSettings, useLocationDisplay } from '@/hooks'
import { useCollisionStore } from '@/stores/collisionStore'
// TODO: temporarily hidden per request — restore later
// import { CollisionPauseToggle } from './components/CollisionPauseToggle'
import { ZoneLabelToggle, nextZoneLabelMode, type ZoneLabelMode } from './components/ZoneLabelToggle'
import { FieldGraphDebugToggle } from './components/FieldGraphDebugToggle'
import { FieldGraphDebugOverlay } from './components/FieldGraphDebugOverlay'
import { TopDownButton } from './components/TopDownButton'
import { useTopDownCamera } from './hooks/useTopDownCamera'
import { useZoneFocusCamera } from './hooks/useZoneFocusCamera'
import { ZoneLabelOverlay } from './components/ZoneLabelOverlay'
import { PlannerDrawer } from './components/PlannerDrawer'
import { TimelineBar } from './components/TimelineBar'
import { EventAircraftPopover } from './components/EventAircraftPopover'
import { CapacityPanel } from './components/CapacityPanel'
import { StatsModal } from './components/StatsModal'
import { PricingSettingsModal } from './components/PricingSettingsModal'
import { ScenarioFleetModal } from './components/ScenarioFleetModal'
import { useEventBootstrap } from './hooks/useEventBootstrap'
import { useEventSceneSync } from './hooks/useEventSceneSync'
import { useEventZoneSync } from './hooks/useEventZoneSync'
import { useEventActorMotion } from './hooks/useEventActorMotion'
import { useEventAircraftPicking } from './hooks/useEventAircraftPicking'
import { useEventSelectionCamera } from './hooks/useEventSelectionCamera'
import { useTaxiPathRibbons } from './hooks/useTaxiPathRibbons'
import { usePlaybackLoop } from './hooks/usePlaybackLoop'
import { useMovementPlans } from './hooks/useMovementPlans'
import { MotionSettingsPanel } from './components/MotionSettingsPanel'
import { useEventPlannerStore } from './store/eventPlannerStore'
import { parseFuelRequest, type FuelRequest } from './motion/fuelRequest'
import { nearestExitNode } from './motion/routeGround'
import { synthesizeFieldGraph, fieldGraphFromPlan } from './data/fieldGraph'
import { buildPlanFromAeroway, fieldGraphFromAeroway } from './data/buildPlanFromAeroway'
import { KAGS_AEROWAY } from './data/kagsAeroway'
import { DEFAULT_VIEW_TIME } from './data/eventWindow'
import { CenterPointToggle } from './components/CenterPointToggle'
import { ToolbarButton, ToolbarDivider } from './components/ToolbarButton'
import { TOOLBAR_BASE, TOOLBAR_OFF, TOOLBAR_ON } from './components/toolbarStyles'
import { AnimationToggle } from './components/AnimationToggle'
import { CenterPointOverlay } from './components/CenterPointOverlay'
import { TaxiEditorToggle } from './components/TaxiEditorToggle'
import { TaxiEditorOverlay } from './components/TaxiEditorOverlay'
import { TaxiEditorPanel } from './components/TaxiEditorPanel'
import { ZonePlacementPanel } from './components/ZonePlacementPanel'
import { useTaxiEditorInteraction } from './hooks/useTaxiEditorInteraction'
import { useTaxiPlanLoader } from './hooks/useTaxiPlanLoader'
import { useTaxiEditorStore } from './store/taxiEditorStore'
import { CinemaToggleButton } from '@/components/cinema'
import { LocationZoomLabel } from '@/components/overlay-ui'
import { FlythroughButton, FlythroughPanel, useFlythroughPlayback } from '@/features/flythrough'

/**
 * Masters Event Planner — experimental, demo-only.
 *
 * Reuses the live `MapboxScene` (KAGS buildings + any real allocations render
 * through the scene's own path) but with deliberately slim chrome: no action
 * island, wizard, filters or edit forms. Event aircraft are pushed imperatively
 * onto the captured `layerService` in later phases (in-memory / localStorage).
 *
 * Collision detection is paused on mount and restored on unmount so the live
 * ramp never inherits a paused collision state.
 */
/** Which panel occupies the right rail (mutually exclusive). */
type RightRail = 'planner' | 'taxi' | 'zone'

export function MastersPlannerPage() {
  const { locationId, locationName } = useAppContext()

  // Name + ICAO for the zoomed-out facility label
  const locationDisplay = useLocationDisplay()

  // Camera/view: reuse the location's saved view so the map opens framed on KAGS.
  const { viewSettings: locationViewSettings, center } = useMapBuildings(locationId)
  const initialViewSettings = useInitialViewSettings(locationId, locationViewSettings)

  // Scene origin as { lat, lng } for the placement engine + zone geometry.
  const mapCenter = useMemo(
    () => (center ? { lat: center[1], lng: center[0] } : undefined),
    [center]
  )

  // Load catalog + zones, generate synthetic inquiries, seed the planner store.
  useEventBootstrap(locationId, mapCenter)

  const [layerService, setLayerService] = useState<MapboxCustomLayerService | null>(null)
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null)
  const [sceneReady, setSceneReady] = useState(false)
  // On-map zone area-code labels: cycle visible → dimmed (80% transparent) → hidden.
  // Default dimmed so codes stay legible without competing with the scene.
  const [labelMode, setLabelMode] = useState<ZoneLabelMode>('dimmed')
  const [showFieldGraph, setShowFieldGraph] = useState(false)
  const [topDown, setTopDown] = useState(false)
  // The right rail shows exactly one of: planner drawer, taxi editor, zone editor.
  // One discriminated state makes that exclusivity structural (no manual toggling).
  const [rightRail, setRightRail] = useState<RightRail>('planner')
  const [showCenter, setShowCenter] = useState(false)
  const [plannerOpen, setPlannerOpen] = useState(true)
  const [statsOpen, setStatsOpen] = useState(false)
  const [pricingOpen, setPricingOpen] = useState(false)
  const [scenarioOpen, setScenarioOpen] = useState(false)
  const [flythroughOpen, setFlythroughOpen] = useState(false)
  const editing = rightRail === 'taxi'
  const zoneEditing = rightRail === 'zone'

  // Camera flythrough — scoped per location so tours don't leak between FBOs.
  const flythroughScope = `event-planner:${locationId ?? 'none'}`
  useFlythroughPlayback({ map: mapInstance, layerService, scope: flythroughScope })

  // When the right rail (planner drawer, taxi editor, or zone editor) is
  // expanded, top-right controls clear it; when collapsed they slide to the edge.
  const rightRailOpen = editing || zoneEditing || plannerOpen
  const rightInset = rightRailOpen ? 'right-[376px]' : 'right-4'

  // Precompute the deterministic motion plan for every placement. Rebuilt only
  // when placements, the motion config, or the authored taxi plan change.
  const placements = useEventPlannerStore((s) => s.placements)
  const motionConfig = useEventPlannerStore((s) => s.motionConfig)
  const zones = useEventPlannerStore((s) => s.zones)
  const zoneBaseGeom = useEventPlannerStore((s) => s.zoneBaseGeom)
  const catalog = useEventPlannerStore((s) => s.catalog)
  const inquiriesById = useEventPlannerStore((s) => s.inquiriesById)
  const currentTime = useEventPlannerStore((s) => s.currentTime)
  const selectedZoneId = useEventPlannerStore((s) => s.selectedZoneId)
  const setSelectedZoneId = useEventPlannerStore((s) => s.setSelectedZoneId)
  // Synthetic field graph (spine/gates), with runway thresholds + bearing aligned
  // to the real primary runway so airborne legs + taxi runway endpoints sit on the
  // actual pavement (matches the imported taxi network).
  const fieldGraph = useMemo(() => {
    // Keep the airborne-final length in sync with the motion config so the descent
    // angle stays consistent (3° / ~3 km) whether or not an authored runway is live.
    const base = synthesizeFieldGraph(zones, { finalRunFt: motionConfig.finalRunFt })
    return mapCenter ? fieldGraphFromAeroway(base, KAGS_AEROWAY, mapCenter, motionConfig.finalRunFt) : base
  }, [zones, mapCenter, motionConfig.finalRunFt])

  // Authored taxi network (drives ground routing whether or not the editor is open).
  const { plans: taxiPlans, activePlanId, setActivePlanId, seedExits, seedFuelDepot } = useTaxiPlanLoader({
    locationId,
    mapCenter,
    fieldGraph,
  })
  const draftPlan = useTaxiEditorStore((s) => s.draftPlan)
  const selectedNodeId = useTaxiEditorStore((s) => s.selectedNodeId)
  const selectedEdgeId = useTaxiEditorStore((s) => s.selectedEdgeId)
  const selectedRunwayId = useTaxiEditorStore((s) => s.selectedRunwayId)
  const edgeAnchorNodeId = useTaxiEditorStore((s) => s.edgeAnchorNodeId)
  const runwayAnchorPos = useTaxiEditorStore((s) => s.runwayAnchorPos)

  // Effective field graph: when the authored plan has a live runway, orient the
  // airborne legs + on-runway roll geometry from its configured landing/takeoff
  // ends. Derived at a stable reference time (not `currentTime`) so plans don't
  // rebuild every tick — runway windows that swap configs mid-event are a future
  // (per-leg) enhancement.
  const effectiveFieldGraph = useMemo(() => {
    const base = draftPlan
      ? fieldGraphFromPlan(draftPlan, DEFAULT_VIEW_TIME, fieldGraph, {
          finalRunFt: motionConfig.finalRunFt,
          touchdownZoneFt: motionConfig.touchdownZoneFt,
          rolloutFraction: motionConfig.rolloutFraction,
          takeoffRollFraction: motionConfig.takeoffRollFraction,
        })
      : fieldGraph
    // Anchor fuel-truck dispatch on the authored depot (real pavement) when one
    // is placed; otherwise keep the synthetic depot.
    return seedFuelDepot ? { ...base, fuelDepot: seedFuelDepot } : base
  }, [draftPlan, fieldGraph, seedFuelDepot, motionConfig.finalRunFt, motionConfig.touchdownZoneFt, motionConfig.rolloutFraction, motionConfig.takeoffRollFraction])

  // Normalized fuel demand per schedule (the demand-driven fuel-truck signal).
  // Each placement links to its inquiry by scheduleId; only fuel-requesting
  // aircraft get a truck.
  const fuelByScheduleId = useMemo(() => {
    const out: Record<string, FuelRequest> = {}
    for (const inq of Object.values(inquiriesById)) {
      const fuel = parseFuelRequest(inq.services)
      if (fuel) out[inq.scheduleId] = fuel
    }
    return out
  }, [inquiriesById])

  // Build movement plans off the main thread (A* routing + path smoothing) so
  // zone / algorithm / motion edits never jank the render; keeps the previous
  // plans visible while a rebuild is in flight, with a synchronous fallback.
  const plans = useMovementPlans({
    placements,
    motionConfig,
    fieldGraph: effectiveFieldGraph,
    catalog,
    taxiPlan: draftPlan,
    zones,
    fuelByScheduleId,
  })

  // Per-exit departure distribution (which exit each parked aircraft is nearest).
  const distribution = useMemo(() => {
    if (!draftPlan) return []
    const counts = new Map<string, number>()
    for (const p of Object.values(placements)) {
      const ex = nearestExitNode(draftPlan, [p.position[0], p.position[1]], p.departure)
      if (ex) counts.set(ex.id, (counts.get(ex.id) ?? 0) + 1)
    }
    return draftPlan.nodes
      .filter((n) => n.role === 'exit_to_taxiway')
      .map((node) => ({ node, count: counts.get(node.id) ?? 0 }))
  }, [draftPlan, placements])

  // Reconcile event placements into the 3D scene (add/remove by visibility).
  useEventSceneSync(layerService, plans, fuelByScheduleId)
  // Bridge live zone move/resize/rotate edits onto the rendered area footprints.
  useEventZoneSync(layerService, zones, zoneBaseGeom)
  // Apply pose(t) to each actor every clock change (play AND scrub).
  useEventActorMotion(layerService, plans)
  // Click a plane in the 3D scene to select it (disabled while an editor is open).
  useEventAircraftPicking({
    map: mapInstance,
    mapCenter,
    plans,
    enabled: rightRail === 'planner',
  })
  // Frame the selected plane in the lower quarter of the screen.
  useEventSelectionCamera({ map: mapInstance, mapCenter, plans })
  // Faint taxi/tow corridors under moving actors (toggle in MotionSettingsPanel).
  useTaxiPathRibbons(layerService, plans)
  // Drive the timeline clock while playing.
  usePlaybackLoop()
  // Top-down, runway-aligned camera for inspecting the taxi network.
  useTopDownCamera(mapInstance, topDown, { zones, fieldGraph: effectiveFieldGraph, center: center ?? null })
  // One-shot fly-to the selected zone (label click or panel dropdown), top-down.
  useZoneFocusCamera(mapInstance, selectedZoneId, zones, center ?? null)
  // Map pointer + keyboard authoring for the taxi editor (active only while editing).
  useTaxiEditorInteraction({ map: mapInstance, center: center ?? null, enabled: editing })

  // Select a right-rail editor (or toggle it off → planner). Exactly one rail is
  // ever active, so opening one structurally closes the other. Entering an editor
  // engages top-down; leaving the taxi editor returns it to Select mode.
  const selectRail = useCallback((rail: 'taxi' | 'zone') => {
    setRightRail((prev) => {
      const target = prev === rail ? 'planner' : rail
      // The taxi editor wants the global top-down; the zone editor instead frames
      // the *selected* zone via useZoneFocusCamera, so don't force top-down here.
      if (target === 'taxi') setTopDown(true)
      if (prev === 'taxi' && target !== 'taxi') useTaxiEditorStore.getState().setMode('view')
      return target
    })
  }, [])
  const toggleEditor = useCallback(() => selectRail('taxi'), [selectRail])
  const toggleZoneEditor = useCallback(() => selectRail('zone'), [selectRail])

  // Header toolbar group fragments, reused inline (wide screens) and stacked
  // inside the overflow popover (narrow screens). `fw` = full-width for the menu.
  const viewGroup = (fw?: boolean) => (
    <>
      <AnimationToggle fullWidth={fw} />
      <TopDownButton active={topDown} onToggle={() => setTopDown((v) => !v)} fullWidth={fw} />
      <ZoneLabelToggle
        mode={labelMode}
        onCycle={() => setLabelMode((m) => nextZoneLabelMode(m))}
        fullWidth={fw}
      />
    </>
  )
  const toolsGroup = (fw?: boolean) => (
    <>
      <ToolbarButton
        icon={<LayoutGrid className="h-4 w-4" />}
        label={fw ? 'Zones' : undefined}
        accent="sky"
        active={zoneEditing}
        fullWidth={fw}
        onClick={toggleZoneEditor}
        title="Zone placement settings"
      />
      <TaxiEditorToggle active={editing} onToggle={toggleEditor} fullWidth={fw} />
      <CenterPointToggle active={showCenter} onToggle={() => setShowCenter((v) => !v)} fullWidth={fw} />
      <FieldGraphDebugToggle
        active={showFieldGraph}
        onToggle={() => setShowFieldGraph((v) => !v)}
        fullWidth={fw}
      />
      <FlythroughButton
        scope={flythroughScope}
        open={flythroughOpen}
        onToggle={() => setFlythroughOpen((v) => !v)}
        compact={!fw}
        fullWidth={fw}
      />
      <CinemaToggleButton compact={!fw} className={fw ? 'w-full justify-start' : undefined} />
    </>
  )
  // Light up the overflow trigger when a collapsed control is in a non-default
  // state, so hidden-but-active settings stay discoverable.
  const anyOverflowActive =
    topDown ||
    showCenter ||
    showFieldGraph ||
    editing ||
    zoneEditing ||
    labelMode !== 'dimmed' ||
    !motionConfig.enableAnimation

  // Regenerate the taxi network from real airport (aeroway) geometry — replaces
  // the draft with a network that sits on the actual runway/taxiway pavement,
  // wiring the apron in via marked exit waypoints. Preserves the current plan's
  // identity so Save updates the same version rather than inserting a duplicate.
  const handleRegenerateGeometry = useCallback(() => {
    if (!locationId || !mapCenter) return
    const built = buildPlanFromAeroway(locationId, KAGS_AEROWAY, mapCenter, {
      exits: seedExits,
      zones,
      name: 'KAGS real-geometry baseline',
    })
    const cur = useTaxiEditorStore.getState().draftPlan
    // Preserve identity (id/locationId/isBaseline) so Save updates the same version;
    // take the freshly built name rather than the prior draft's placeholder.
    const next = cur
      ? { ...built, id: cur.id, locationId: cur.locationId, isBaseline: cur.isBaseline }
      : built
    useTaxiEditorStore.getState().replaceDraft(next)
  }, [locationId, mapCenter, seedExits, zones])

  // Pause collisions while the planner is open; restore the prior value on exit.
  useEffect(() => {
    const prev = useCollisionStore.getState().isEnabled
    useCollisionStore.getState().setEnabled(false)
    return () => {
      useCollisionStore.getState().setEnabled(prev)
    }
  }, [])

  const handleSceneReady = useCallback((refs: MapboxSceneRefs) => {
    setLayerService(refs.layerService)
    setMapInstance(refs.map)
    setSceneReady(true)
  }, [])

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-950">
      <MapboxScene
        className="h-full w-full"
        locationId={locationId}
        initialViewSettings={initialViewSettings}
        enableShadows
        interactionEnabled={false}
        onReady={handleSceneReady}
        renderOverlay={(map) => (
          <div className="pointer-events-none absolute inset-0">
            {/* On-map area-code labels (click to select + configure a zone).
                Visibility cycles via the Labels toggle: visible / dimmed / hidden. */}
            <ZoneLabelOverlay
              map={map}
              center={center ?? null}
              zones={zones}
              selectedZoneId={selectedZoneId}
              mode={labelMode}
              onSelect={(id) => {
                setSelectedZoneId(id)
                setRightRail('zone')
              }}
            />

            {/* Synthetic airfield network: runway, spine, gates, routed taxi paths */}
            <FieldGraphDebugOverlay
              map={map}
              center={center ?? null}
              fieldGraph={effectiveFieldGraph}
              plans={plans}
              visible={showFieldGraph}
            />

            {/* Location center (scene origin) marker */}
            <CenterPointOverlay map={map} center={center ?? null} visible={showCenter} />

            {/* Facility name — fades in once the camera pulls back past the
                point where the field itself is legible. */}
            <LocationZoomLabel
              map={map}
              center={center ?? null}
              name={locationDisplay.name}
              code={locationDisplay.code}
            />

            {/* Editable taxi waypoint network (nodes + edges) */}
            <TaxiEditorOverlay
              map={map}
              center={center ?? null}
              plan={draftPlan}
              currentTime={currentTime}
              selectedNodeId={selectedNodeId}
              selectedEdgeId={selectedEdgeId}
              selectedRunwayId={selectedRunwayId}
              edgeAnchorNodeId={edgeAnchorNodeId}
              runwayAnchorPos={runwayAnchorPos}
              visible={editing}
            />

            {/* Top-left: title + back */}
            <div className="absolute left-4 top-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-black/60 text-white/70 backdrop-blur-sm transition-colors hover:text-white"
                title="Back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="rounded-lg border border-white/10 bg-black/60 px-3 py-1.5 backdrop-blur-sm">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-white/30">
                  Masters Event Planner
                </div>
                <div className="text-sm font-bold text-white">
                  {locationName ?? 'KAGS'} — Sales Demo
                </div>
              </div>
            </div>

            {/* Top-right: view toggles + collision pause.
                Slides to the screen edge when the right rail is collapsed. */}
            <div className={`absolute ${rightInset} top-4 flex items-center gap-1.5`}>
              {/* Group 1 — Actions / panels (always inline) */}
              <ToolbarButton
                icon={<BarChart3 className="h-4 w-4" />}
                label="Stats"
                accent="sky"
                always
                onClick={() => setStatsOpen(true)}
                title="Event statistics"
              />
              <ToolbarButton
                icon={<DollarSign className="h-4 w-4" />}
                label="Pricing"
                accent="emerald"
                always
                onClick={() => setPricingOpen(true)}
                title="Event pricing"
              />
              <ToolbarButton
                icon={<Plane className="h-4 w-4" />}
                label="Fleet"
                accent="sky"
                always
                onClick={() => setScenarioOpen(true)}
                title="Scenario / fleet — count, type mix, timing, seed"
              />

              {/* Wide screens: View + Tools groups inline with dividers. */}
              <div className="hidden items-center gap-1.5 xl:flex">
                <ToolbarDivider />
                {viewGroup()}
                <ToolbarDivider />
                {toolsGroup()}
              </div>

              {/* Narrow screens: collapse View + Tools into an overflow popover. */}
              <div className="flex xl:hidden">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      title="More controls"
                      className={cn(
                        TOOLBAR_BASE,
                        'w-9 justify-center px-0',
                        anyOverflowActive ? TOOLBAR_ON.sky : TOOLBAR_OFF,
                      )}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="flex w-auto min-w-[11rem] flex-col gap-1.5 border-white/10 bg-black/80 p-2 backdrop-blur-md"
                  >
                    {viewGroup(true)}
                    <div className="my-0.5 h-px w-full bg-white/10" />
                    {toolsGroup(true)}
                  </PopoverContent>
                </Popover>
              </div>
              {/* TODO: temporarily hidden per request — restore later */}
              {/* <CollisionPauseToggle /> */}
            </div>

            {/* Motion controls + live taxiing/tug readout — only relevant when
                animations are on (the toolbar toggle handles on/off). */}
            {motionConfig.enableAnimation && (
              <div className={`absolute ${rightInset} top-14`}>
                <MotionSettingsPanel plans={plans} />
              </div>
            )}

            {/* Camera flythrough authoring — floats under the toolbar, clear of
                the right rail (which the toolbar inset already tracks). */}
            {flythroughOpen && (
              <div className={`absolute ${rightInset} top-14 z-30`}>
                <FlythroughPanel
                  map={map}
                  scope={flythroughScope}
                  onClose={() => setFlythroughOpen(false)}
                />
              </div>
            )}

            {/* Left: capacity dashboard (time-not-space view) */}
            <CapacityPanel onExpand={() => setStatsOpen(true)} />

            {/* Right rail: taxi editor, zone-placement editor, or planner drawer */}
            {editing ? (
              <TaxiEditorPanel
                plans={taxiPlans}
                activePlanId={activePlanId}
                onSelectPlan={setActivePlanId}
                distribution={distribution}
                onRegenerateFromGeometry={handleRegenerateGeometry}
              />
            ) : zoneEditing ? (
              <ZonePlacementPanel onClose={() => setRightRail('planner')} center={center ?? null} />
            ) : (
              <PlannerDrawer open={plannerOpen} onOpenChange={setPlannerOpen} />
            )}

            {/* Bottom: timeline scrubber + playback */}
            <TimelineBar />

            {/* Selected aircraft info — anchored above the plane (3D click or drawer) */}
            <EventAircraftPopover map={map} mapCenter={mapCenter} plans={plans} />

            {/* Sales-demo stats + pricing modals (portal to body, above the planner) */}
            <StatsModal open={statsOpen} onOpenChange={setStatsOpen} />
            <PricingSettingsModal open={pricingOpen} onOpenChange={setPricingOpen} />
            <ScenarioFleetModal open={scenarioOpen} onOpenChange={setScenarioOpen} />
          </div>
        )}
      />

      {!sceneReady && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950">
          <div className="space-y-3 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-sky-500/30 border-t-sky-400" />
            <div className="text-sm text-white/60">Loading event planner…</div>
          </div>
        </div>
      )}
    </div>
  )
}
